import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
} from 'react';

// ── Botão (UX-PAINEL-INTERNO §0.5) ────────────────────────────────────────────
type VarianteBotao = 'primario' | 'secundario' | 'fantasma' | 'perigo';

interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  carregando?: boolean;
}

export const Botao = forwardRef<HTMLButtonElement, BotaoProps>(function Botao(
  { variante = 'primario', carregando = false, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={clsx(
        'inline-flex h-10 items-center justify-center gap-2 rounded-1 px-4 font-display text-[0.9375rem] font-bold tracking-[0.01em] transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variante === 'primario' && 'bg-acento text-white hover:bg-acento-pressed',
        variante === 'secundario' &&
          'border border-linha-forte bg-superficie-alta text-tinta-forte hover:bg-superficie',
        variante === 'fantasma' && 'text-tinta-2 hover:text-tinta-forte',
        variante === 'perigo' && 'border border-erro text-erro hover:bg-aviso-fraco',
        className,
      )}
      aria-busy={carregando || undefined}
      disabled={disabled ?? carregando}
      {...rest}
    >
      {carregando && (
        <span
          aria-hidden
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});

// ── Campo de texto (rótulo acima, erro inline — §0.5) ─────────────────────────
interface CampoProps extends InputHTMLAttributes<HTMLInputElement> {
  rotulo: string;
  ajuda?: string;
  erro?: string;
  obrigatorio?: boolean;
}

export const Campo = forwardRef<HTMLInputElement, CampoProps>(function Campo(
  { rotulo, ajuda, erro, obrigatorio, className, id, ...rest },
  ref,
) {
  const gerado = useId();
  const campoId = id ?? gerado;
  const ajudaId = `${campoId}-ajuda`;
  const erroId = `${campoId}-erro`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={campoId} className="text-corpo text-tinta-2">
        {rotulo}
        {obrigatorio && <span className="text-erro"> *</span>}
      </label>
      <input
        ref={ref}
        id={campoId}
        className={clsx(
          'h-10 rounded-1 border bg-superficie-alta px-3 text-corpo text-tinta-forte',
          erro ? 'border-erro focus-visible:outline-none' : 'border-linha-forte',
          className,
        )}
        aria-invalid={erro ? true : undefined}
        aria-describedby={clsx(ajuda && ajudaId, erro && erroId) || undefined}
        {...rest}
      />
      {ajuda && !erro && (
        <span id={ajudaId} className="text-nota text-tinta-2">
          {ajuda}
        </span>
      )}
      {erro && (
        <span id={erroId} className="text-nota text-erro">
          {erro}
        </span>
      )}
    </div>
  );
});

interface AreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  rotulo: string;
  ajuda?: string;
  erro?: string;
  obrigatorio?: boolean;
}

export const AreaTexto = forwardRef<HTMLTextAreaElement, AreaProps>(function AreaTexto(
  { rotulo, ajuda, erro, obrigatorio, className, id, ...rest },
  ref,
) {
  const gerado = useId();
  const campoId = id ?? gerado;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={campoId} className="text-corpo text-tinta-2">
        {rotulo}
        {obrigatorio && <span className="text-erro"> *</span>}
      </label>
      <textarea
        ref={ref}
        id={campoId}
        className={clsx(
          'min-h-20 rounded-1 border bg-superficie-alta px-3 py-2 text-corpo text-tinta-forte',
          erro ? 'border-erro' : 'border-linha-forte',
          className,
        )}
        aria-invalid={erro ? true : undefined}
        {...rest}
      />
      {ajuda && !erro && <span className="text-nota text-tinta-2">{ajuda}</span>}
      {erro && <span className="text-nota text-erro">{erro}</span>}
    </div>
  );
});

// ── Segmented control (escolha explícita — §0.5 / §4.2) ───────────────────────
interface SegmentadoProps<T extends string> {
  rotulo: string;
  opcoes: { valor: T; texto: string }[];
  valor: T | null;
  aoMudar: (v: T) => void;
  erro?: string;
  nota?: string;
}

export function Segmentado<T extends string>({
  rotulo,
  opcoes,
  valor,
  aoMudar,
  erro,
  nota,
}: SegmentadoProps<T>): ReactNode {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-corpo text-tinta-2">{rotulo}</legend>
      <div className="flex flex-wrap gap-2">
        {opcoes.map((o) => (
          <button
            key={o.valor}
            type="button"
            aria-pressed={valor === o.valor}
            onClick={() => aoMudar(o.valor)}
            className={clsx(
              'h-10 rounded-1 border px-3 text-corpo transition-colors',
              valor === o.valor
                ? 'border-acento bg-acento-fraco text-tinta-forte'
                : 'border-linha-forte bg-superficie-alta text-tinta-2 hover:text-tinta-forte',
            )}
          >
            {o.texto}
          </button>
        ))}
      </div>
      {nota && <span className="text-nota text-tinta-2">{nota}</span>}
      {erro && <span className="text-nota text-erro">{erro}</span>}
    </fieldset>
  );
}

// ── Chip de status (§0.3 rótulo) ─────────────────────────────────────────────
type TomChip = 'neutro' | 'acento' | 'sucesso' | 'aviso' | 'apagado';

export function Chip({
  children,
  tom = 'neutro',
}: {
  children: ReactNode;
  tom?: TomChip;
}): ReactNode {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-1 border px-1.5 py-0.5 font-display text-rotulo uppercase',
        tom === 'neutro' && 'border-linha-forte text-tinta-2',
        tom === 'acento' && 'border-acento-borda text-acento-texto',
        tom === 'sucesso' && 'border-sucesso text-sucesso',
        tom === 'aviso' && 'border-aviso text-aviso',
        tom === 'apagado' && 'border-linha text-tinta-3',
      )}
    >
      {children}
    </span>
  );
}

// ── Placa do código (DESIGN-FOUNDATION §5.2, variante cheia) ──────────────────
export function PlacaCodigo({
  codigo,
  tamanho = 'md',
}: {
  codigo: string;
  tamanho?: 'sm' | 'md';
}): ReactNode {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-3 bg-grafite px-3 py-1.5 font-codigo text-placa-tinta',
        'shadow-[inset_0_1px_0_rgba(255,255,255,.14)]',
        tamanho === 'md' ? 'text-[0.9375rem]' : 'text-[0.8125rem]',
      )}
    >
      {codigo}
    </span>
  );
}

// ── Blocos de sistema (§0.6) ─────────────────────────────────────────────────
export function BlocoAviso({
  children,
  tom = 'aviso',
  aoTentarDeNovo,
}: {
  children: ReactNode;
  tom?: 'aviso' | 'erro';
  aoTentarDeNovo?: () => void;
}): ReactNode {
  return (
    <div
      className={clsx(
        'rounded-2 border px-4 py-3 text-corpo text-tinta-forte',
        tom === 'aviso' ? 'border-aviso bg-aviso-fraco' : 'border-erro bg-aviso-fraco',
      )}
    >
      <p className="max-w-[72ch]">{children}</p>
      {aoTentarDeNovo && (
        <button
          type="button"
          onClick={aoTentarDeNovo}
          className="mt-2 font-display text-[0.8125rem] font-bold text-acento-texto underline"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}

export function EstadoVazio({
  titulo,
  children,
}: {
  titulo: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-2 border border-linha bg-superficie px-4 py-8 text-center">
      <p className="text-corpo text-tinta-forte">{titulo}</p>
      {children && <div className="mt-3 flex justify-center gap-2 text-corpo">{children}</div>}
    </div>
  );
}

// ── Linhas-esqueleto de tabela (§0.4 — sem shimmer) ──────────────────────────
export function LinhasEsqueleto({
  linhas = 6,
  colunas = 4,
}: {
  linhas?: number;
  colunas?: number;
}): ReactNode {
  return (
    <div className="flex flex-col">
      {Array.from({ length: linhas }).map((_, i) => (
        <div key={i} className="flex h-11 items-center gap-4 border-b border-linha px-3">
          {Array.from({ length: colunas }).map((__, j) => (
            <div key={j} className="h-3 flex-1 rounded-1 bg-poco" />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Barra de paginação (§0.4) ────────────────────────────────────────────────
export function PaginacaoBarra({
  pagina,
  limite,
  total,
  aoIr,
}: {
  pagina: number;
  limite: number;
  total: number;
  aoIr: (p: number) => void;
}): ReactNode {
  const inicio = total === 0 ? 0 : (pagina - 1) * limite + 1;
  const fim = Math.min(pagina * limite, total);
  const ultima = Math.max(1, Math.ceil(total / limite));
  return (
    <div className="flex items-center justify-between py-3 text-nota text-tinta-2">
      <span className="tnum">
        mostrando {inicio}–{fim} de {total}
      </span>
      <div className="flex gap-2">
        <Botao
          variante="secundario"
          className="h-8 px-3 text-[0.8125rem]"
          disabled={pagina <= 1}
          onClick={() => aoIr(pagina - 1)}
        >
          Anterior
        </Botao>
        <Botao
          variante="secundario"
          className="h-8 px-3 text-[0.8125rem]"
          disabled={pagina >= ultima}
          onClick={() => aoIr(pagina + 1)}
        >
          Próxima
        </Botao>
      </div>
    </div>
  );
}
